import { ShieldCheck, Boxes, CheckCircle2, FileText, User } from "lucide-react";

const STORY_IMG = "/img/project-solar-ic.png";

const VALUES = [
  { i: ShieldCheck, t: "Safety first.", d: "PPE discipline, toolbox talks and permit-to-work practices on every site, every day." },
  { i: Boxes, t: "Own what matters.", d: "Machines, instruments, manpower and accountability — on our own books." },
  { i: CheckCircle2, t: "Finish what we start.", d: "Scopes closed, punch points cleared, sites handed over clean." },
  { i: FileText, t: "Document everything.", d: "Test reports, checklists and as-built records that survive an audit." },
];

export function AboutHero() {
  return (
    <section className="ye-page-hero">
      <div className="ye-container">
        <span className="ye-eyebrow">About Us</span>
        <h1 className="ye-display ye-h1" style={{ margin: "8px 0 0" }}>
          An Execution Company From the Heart of India&apos;s Solar Belt
        </h1>
      </div>
    </section>
  );
}

export function CompanyStory() {
  return (
    <section className="ye-section">
      <div className="ye-container ye-grid-2" style={{ gap: 56, alignItems: "center" }}>
        <div className="ye-prose">
          <span className="ye-eyebrow">Our Story</span>
          <p style={{ marginTop: 6 }}>
            Yaduraj Enterprises was built in Sheo, Barmer — Rajasthan&apos;s high-radiation solar corridor, where some of India&apos;s largest solar parks rise out of hard desert terrain. We started where every solar plant actually starts: on the ground, with electrical works, earth work and installation crews.
          </p>
          <p>Led by Managing Director Ran Singh, an expert electrical contractor, we grew the way strong site teams grow — by owning our machines instead of renting them, by keeping skilled manpower on our own rolls, and by finishing scopes that others underestimate. Today we deliver installation &amp; commissioning, EPC balance-of-plant and electrical contracting for solar and battery energy storage projects, working with developers, EPC companies and industrial clients.</p>
          <p>Our registered office remains in Barmer, minutes from the terrain we know best, with a second office in New Delhi for client coordination across India.</p>
        </div>
        <div className="ye-photo" style={{ aspectRatio: "4/3" }}>
          <img loading="lazy" src={STORY_IMG} alt="Solar plant in desert terrain, Rajasthan" />
        </div>
      </div>
    </section>
  );
}

export function LeadershipCard() {
  return (
    <section className="ye-section ye-section--bg">
      <div className="ye-container">
        <div className="ye-card" style={{ padding: 36, display: "grid", gridTemplateColumns: "84px 1fr", gap: 24, alignItems: "start" }}>
          <div style={{ width: 84, height: 84, borderRadius: 12, background: "rgba(27,75,140,0.08)", color: "var(--navy)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <User size={40} />
          </div>
          <div>
            <span className="ye-eyebrow">Leadership</span>
            <h2 className="ye-display ye-h3" style={{ margin: "4px 0 12px", fontSize: "1.6rem" }}>Ran Singh — Managing Director</h2>
            <p style={{ margin: 0 }}>
              An expert electrical contractor by trade, Ran Singh personally leads project execution — from piling campaigns and structure erection to HT terminations and final commissioning. His ground-up approach is the reason Yaduraj teams arrive with the right machine, the right instrument and the right test format the first time.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function ValueCard({ icon: Icon, title, description }) {
  return (
    <div className="ye-card">
      <div style={{ width: 48, height: 48, borderRadius: 8, background: "rgba(245,130,32,0.10)", color: "var(--orange)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
        <Icon size={24} />
      </div>
      <h3 className="ye-h3" style={{ margin: "0 0 6px" }}>{title}</h3>
      <p style={{ margin: 0, fontSize: 14.5 }}>{description}</p>
    </div>
  );
}

export function ValuesGrid() {
  return (
    <section className="ye-section">
      <div className="ye-container">
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <span className="ye-eyebrow">What We Stand For</span>
          <h2 className="ye-display ye-h2" style={{ margin: "6px 0 0" }}>Our Values</h2>
        </div>
        <div className="ye-grid-4">
          {VALUES.map((v) => <ValueCard key={v.t} icon={v.i} title={v.t} description={v.d} />)}
        </div>
      </div>
    </section>
  );
}

export function HSESystem() {
  return (
    <section className="ye-section ye-section--bg">
      <div className="ye-container" style={{ maxWidth: 980 }}>
        <h2 className="ye-display ye-h2" style={{ margin: "0 0 16px" }}>Safety & Quality, Run Like a System</h2>
        <p style={{ margin: 0, fontSize: 17 }}>
          Every Yaduraj site runs on daily toolbox talks, enforced PPE, permit-to-work for electrical activities and supervised lifting operations. On the quality side, we work to approved drawings and method statements, maintain calibrated instruments — meggers, earth testers, torque tools — and close every scope with IR test reports, pre-commissioning checklists and a complete handover dossier.
        </p>
      </div>
    </section>
  );
}

export function WhereWeWork() {
  return (
    <section className="ye-section">
      <div className="ye-container" style={{ maxWidth: 980 }}>
        <h2 className="ye-display ye-h2" style={{ margin: "0 0 16px" }}>Rooted in Rajasthan. Mobile Across India.</h2>
        <p style={{ margin: 0, fontSize: 17 }}>
          Our home ground is Rajasthan&apos;s solar corridor — Barmer, Jaisalmer, Jodhpur and Bikaner — where our fleet and crews are based. With our own site campers and transport, we mobilise self-sufficient teams to project locations across India, coordinated through our Barmer and New Delhi offices.
        </p>
      </div>
    </section>
  );
}
